import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getEnvironmentInfo, apiRequest } from '../config/api';
import { getCurrentTimestamp } from '../utils/dateUtils';
import './styles/EmailInvoice.css';

// Environment-aware n8n webhook URL configuration
const getN8nWebhookUrl = () => {
  const envInfo = getEnvironmentInfo();
  
  switch (envInfo.environment) {
    case 'development':
      return "https://gmc-space.app.n8n.cloud/webhook-test/send-invoice-email";
    case 'production':
      return "https://gmc-space.app.n8n.cloud/webhook/send-invoice-email";
    case 'staging':
    default:
      return "https://gmc-space.app.n8n.cloud/webhook/send-invoice-email";
  }
};

const EmailInvoice = ({ 
  // Required props
  orderDetails,
  
  // Pre-loaded data (when used from OrderReceipt)
  customerInfo = null,
  orderItems = [],
  payments = [],
  lineItemsData = null,
  paymentsData = null,
  receiptHtml = null, // For when we already have the HTML
  
  // Optional props
  onSuccess = () => {},
  onError = () => {},
  buttonText = "📧 Email Invoice",
  buttonClassName = "email-invoice-button",
  disabled = false,
  modalTitle = "Send Invoice Email",
  webhookUrl = null
}) => {
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);
  
  // Editable customer info for email
  const [emailCustomerInfo, setEmailCustomerInfo] = useState(null);
  const [customMessage, setCustomMessage] = useState('');
  
  // Environment detection
  const envInfo = getEnvironmentInfo();
  const finalWebhookUrl = webhookUrl || getN8nWebhookUrl();
  
  // Get order number
  const orderNumber = orderDetails?.orderNo || orderDetails?.OrderNo || orderDetails?.orderNumber;

  // Initialize editable customer info when modal opens
  useEffect(() => {
    if (isModalOpen && customerInfo) {
      setEmailCustomerInfo({
        CustomerEmail: customerInfo.CustomerEmail || '',
        CustomerFullName: customerInfo.CustomerFullName || 
                         customerInfo.customerName || 
                         `${customerInfo.CustomerFirstName || ''} ${customerInfo.CustomerLastName || ''}`.trim(),
        CustomerPhone: customerInfo.CustomerPhone || customerInfo.phone || '',
        CustomerAddress: customerInfo.CustomerAddress || customerInfo.address || '',
        CustomerCity: customerInfo.CustomerCity || customerInfo.city || '',
        CustomerState: customerInfo.CustomerState || customerInfo.state || '',
        CustomerZipCode: customerInfo.CustomerZipCode || customerInfo.zip || ''
      });
    }
  }, [isModalOpen, customerInfo]);

  useEffect(() => {
  if (isModalOpen) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = 'unset';
  }
  
  // Cleanup
  return () => {
    document.body.style.overflow = 'unset';
  };
}, [isModalOpen]);

  // Generate receipt HTML - with embedded logo and better readability
  const generateReceiptHtml = async () => {
    if (receiptHtml) {
      return receiptHtml;
    }

    const receiptElement = document.querySelector('.receipt-container');
    if (!receiptElement) {
      throw new Error('Could not find receipt content on page');
    }

    // Clone the element to modify it without affecting the page
    const clonedElement = receiptElement.cloneNode(true);

    // Convert logo image to base64 for embedding
    const logoImg = clonedElement.querySelector('.company-logo-right img');
    if (logoImg) {
      try {
        // Fetch the logo and convert to base64
        const logoResponse = await fetch('/images/dini_title.png');
        const logoBlob = await logoResponse.blob();
        const logoBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(logoBlob);
        });
        logoImg.src = logoBase64;
      } catch (error) {
        console.warn('Could not load logo:', error);
      }
    }

    // Enhanced print styles with larger, darker text
    const printStyles = `
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Playfair+Display:wght@400;600;700&display=swap');

        @page { margin: 0.5in; size: letter; }

        :root {
          --dini-pink: #FF68C5; --dini-pink-light: #FF98D9; --dini-pink-dark: #E91E63;
          --text-dark: #000000; --text-light: #333333; --border-light: #e0e0e0;
          --bg-white: #ffffff; --bg-light: #fafafa;
        }

        body { margin: 0 !important; padding: 0 !important; font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important; background: var(--bg-white) !important; }
        .no-print { display: none !important; }
        .receipt-container { background: var(--bg-white) !important; color: var(--text-dark) !important; font-family: 'Inter', sans-serif !important; max-width: 100% !important; margin: 0 !important; padding: 20px !important; line-height: 1.5 !important; font-size: 13px !important; position: relative !important; }
        .modern-header { display: flex !important; justify-content: space-between !important; align-items: flex-start !important; margin-bottom: 25px !important; padding-bottom: 15px !important; border-bottom: 2px solid var(--dini-pink) !important; padding: 20px !important; margin: -20px -20px 25px -20px !important; border-radius: 0 0 12px 12px !important; }
        .company-info-left { font-family: 'Inter', sans-serif !important; font-size: 11px !important; line-height: 1.4 !important; color: var(--text-dark) !important; font-weight: 400 !important; max-width: 200px !important; }
        .company-info-left strong { color: var(--text-dark) !important; font-weight: 600 !important; }
        .invoice-title-large { font-family: 'Playfair Display', serif !important; font-size: 42px !important; font-weight: 700 !important; color: var(--text-dark) !important; margin: 0 !important; letter-spacing: -1.2px !important; line-height: 0.85 !important; }
        .details-billing-container { display: flex !important; gap: 20px !important; margin-bottom: 20px !important; }
        .invoice-details { flex: 1 !important; font-family: 'Inter', sans-serif !important; font-size: 13px !important; line-height: 1.5 !important; background: var(--bg-light) !important; padding: 12px !important; border-radius: 6px !important; border-left: 3px solid var(--dini-pink) !important; }
        .invoice-details div { margin-bottom: 3px !important; display: flex !important; justify-content: space-between !important; }
        .invoice-details strong { color: var(--text-dark) !important; font-weight: 600 !important; }
        .bill-to-section { flex: 1 !important; font-family: 'Inter', sans-serif !important; font-size: 13px !important; line-height: 1.4 !important; background: var(--bg-white) !important; border: 1px solid var(--border-light) !important; padding: 12px !important; border-radius: 6px !important; }
        .bill-to-title { font-weight: 700 !important; margin-bottom: 8px !important; font-size: 11px !important; color: var(--text-dark) !important; letter-spacing: 0.3px !important; text-transform: uppercase !important; border-bottom: 1px solid var(--dini-pink-light) !important; padding-bottom: 4px !important; }
        .bill-to-section div:not(.bill-to-title) { margin-bottom: 3px !important; color: var(--text-dark) !important; }
        .modern-table { width: 100% !important; border-collapse: collapse !important; margin-bottom: 20px !important; border-radius: 12px !important; overflow: hidden !important; box-shadow: 0 2px 8px rgba(26, 26, 26, 0.1) !important; }
        .modern-table th { background: linear-gradient(135deg, var(--dini-pink), var(--dini-pink-dark)) !important; color: white !important; padding: 12px 10px !important; font-weight: 600 !important; font-family: 'Inter', sans-serif !important; font-size: 11px !important; text-align: left !important; letter-spacing: 0.5px !important; text-transform: uppercase !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        .modern-table th:last-child { text-align: right !important; }
        .modern-table td { padding: 10px !important; border-bottom: 1px solid var(--border-light) !important; font-family: 'Inter', sans-serif !important; font-size: 12px !important; vertical-align: top !important; background: white !important; color: var(--text-dark) !important; }
        .modern-table td:last-child { text-align: right !important; font-weight: 600 !important; color: var(--text-dark) !important; }
        .modern-table td:nth-child(2) { text-align: center !important; font-weight: 500 !important; }
        .modern-table td:nth-child(3) { text-align: right !important; color: var(--text-dark) !important; }
        .modern-table tbody tr:nth-child(even) { background: var(--bg-light) !important; }
        .modern-table tbody tr:last-child td { border-bottom: 2px solid var(--dini-pink) !important; }
        .totals-section-modern { margin-top: 15px !important; padding: 15px !important; background: var(--bg-light) !important; border: 1px solid var(--border-light) !important; border-radius: 8px !important; max-width: 280px !important; margin-left: auto !important; }
        .total-line-modern { display: flex !important; justify-content: space-between !important; align-items: center !important; margin-bottom: 5px !important; font-family: 'Inter', sans-serif !important; font-size: 12px !important; padding: 3px 0 !important; }
        .total-line-modern span:first-child { color: var(--text-dark) !important; font-weight: 500 !important; }
        .total-line-modern span:last-child { font-weight: 600 !important; color: var(--text-dark) !important; }
        .total-line-modern.final { font-weight: 700 !important; font-size: 14px !important; margin-top: 10px !important; padding-top: 10px !important; border-top: 2px solid var(--dini-pink) !important; color: var(--text-dark) !important; background: var(--dini-pink-light) !important; background-opacity: 0.15 !important; padding: 10px 8px !important; border-radius: 4px !important; }
        .total-line-modern.final span { color: var(--text-dark) !important; font-weight: 700 !important; }
        .warranty-section { margin: 10px 0 !important; padding: 10px !important; background: var(--bg-light) !important; border: 1px solid var(--border-light) !important; border-radius: 4px !important; font-family: 'Inter', sans-serif !important; font-size: 9px !important; line-height: 1.3 !important; text-align: center !important; position: relative !important; page-break-inside: avoid !important; }
        .warranty-title { font-family: 'Playfair Display', serif !important; font-weight: 600 !important; margin-bottom: 6px !important; font-size: 13px !important; color: var(--text-dark) !important; font-style: italic !important; margin-top: 2px !important; }
        .warranty-text { color: var(--text-dark) !important; font-style: italic !important; font-size: 11px !important; max-width: 800px !important; margin: 0 auto !important; line-height: 1.3 !important; }
      </style>
    `;

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice - Order #${orderNumber}</title>
          <meta charset="utf-8">
          ${printStyles}
        </head>
        <body>
          ${clonedElement.outerHTML}
        </body>
      </html>
    `;
  };

  const extractInvoiceSummary = (invoiceHtml) => {
  if (!invoiceHtml) return 'Invoice details not available';
  
  // Create a temporary div to parse the HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = invoiceHtml;
  
  // Extract key information (customize based on your invoice structure)
  const orderTotal = tempDiv.querySelector('.total, .grand-total, [class*="total"]')?.textContent || '';
  const items = Array.from(tempDiv.querySelectorAll('.line-item, .item, tr')).slice(0, 5); // Limit to 5 items
  const customerInfo = tempDiv.querySelector('.customer-info-email, .billing-info')?.textContent || '';
  
  // Build a concise summary
  let summary = '<div style="font-family: Arial, sans-serif;">';
  
  // Add items summary
  if (items.length > 0) {
    summary += '<h4 style="margin: 0 0 10px 0; color: #2c2c2c;">Order Items:</h4>';
    summary += '<ul style="margin: 0; padding-left: 20px;">';
    
    items.forEach(item => {
      const text = item.textContent?.trim();
      if (text && text.length > 10) {
        summary += `<li style="margin: 5px 0;">${text.substring(0, 100)}${text.length > 100 ? '...' : ''}</li>`;
      }
    });
    
    summary += '</ul>';
  }
  
  // Add total if found
  if (orderTotal) {
    summary += `<div style="margin: 15px 0; padding: 10px; background: #f8f8f8; border-radius: 5px;">
      <strong style="color: #d4af37;">Total: ${orderTotal}</strong>
    </div>`;
  }
  
  // Add note about full details
  summary += '<p style="margin: 15px 0 0 0; font-size: 14px; color: #666; font-style: italic;">This is a summary. Full invoice details are available in your customer portal.</p>';
  
  summary += '</div>';
  
  return summary;
};

const sendEmail = async () => {
  setIsSending(true);
  setError(null);

  try {
    // Validate required data
    if (!emailCustomerInfo?.CustomerEmail) {
      throw new Error('Customer email address is required');
    }

    if (!orderNumber) {
      throw new Error('Order number is required');
    }

    console.log('📧 Preparing to send email:', {
      environment: envInfo.environment,
      webhookUrl: finalWebhookUrl,
      customerEmail: emailCustomerInfo.CustomerEmail,
      orderNumber: orderNumber
    });

    // Generate HTML for the invoice (async to load logo)
    console.log('📄 Generating invoice HTML with embedded logo...');
    const invoiceHtml = await generateReceiptHtml();

    console.log('📄 Generating PDF from invoice HTML...');

    // Call backend API to generate PDF as base64
    const pdfResponse = await apiRequest(`${envInfo.apiUrl}/api/invoice/generate-pdf-base64`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        invoiceHtml: invoiceHtml,
        orderNumber: orderNumber,
        options: {
          format: 'Letter',
          margin: {
            top: '0.5in',
            right: '0.5in',
            bottom: '0.5in',
            left: '0.5in'
          }
        }
      })
    });

    if (!pdfResponse.ok) {
      throw new Error(`Failed to generate PDF: ${pdfResponse.statusText}`);
    }

    const pdfData = await pdfResponse.json();

    if (!pdfData.success) {
      throw new Error(pdfData.message || 'Failed to generate PDF');
    }

    console.log('✅ PDF generated successfully:', {
      filename: pdfData.filename,
      size: pdfData.size
    });

    // Prepare data for n8n webhook
    const emailData = {
      // Customer info
      customerEmail: emailCustomerInfo.CustomerEmail,
      customerName: emailCustomerInfo.CustomerFullName || 'Valued Customer',
      customerId: orderDetails?.customerNo || orderDetails?.OrderCustomerNo,
      // Order info
      orderNumber: orderNumber,
      orderType: orderDetails?.orderType || orderDetails?.OrderType,

      // PDF attachment (base64 encoded)
      pdfAttachment: {
        filename: pdfData.filename,
        content: pdfData.pdfBase64,
        encoding: 'base64',
        contentType: 'application/pdf'
      },

      customMessage: customMessage.trim(),

      // Environment and routing info
      environment: envInfo.environment,
      sentAt: getCurrentTimestamp(),
      sentFrom: 'DiniTracker Application',

      // Additional metadata for n8n processing
      routing: {
        environment: envInfo.environment,
        hostname: envInfo.hostname,
        timestamp: getCurrentTimestamp()
      },

      // Include the order and customer data for n8n processing
      orderDetails: orderDetails,
      customerInfo: emailCustomerInfo
    };

    console.log('📧 Sending email via n8n webhook...');

    // SAFER RESPONSE HANDLING
    const response = await apiRequest(finalWebhookUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Environment': envInfo.environment,
        'X-Source': 'DiniTracker-EmailInvoice'
      },
      body: JSON.stringify(emailData)
    });

    // Check response status first
    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      
      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } else {
          const errorText = await response.text();
          errorMessage = errorText || errorMessage;
        }
      } catch (parseError) {
        console.warn('Could not parse error response:', parseError);
      }
      
      throw new Error(errorMessage);
    }

    // Handle successful response safely
    let result = { success: true, message: 'Email sent successfully' };
    
    try {
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        const responseText = await response.text();
        
        if (responseText && responseText.trim()) {
          result = JSON.parse(responseText);
        }
      } else {
        // Non-JSON response, but status was ok
        const responseText = await response.text();
        console.log('Non-JSON response received:', responseText);
      }
    } catch (parseError) {
      console.warn('Could not parse success response JSON, but request succeeded:', parseError);
      // Continue with default success result since the HTTP status was ok
    }

    // Validate the result
    if (result && result.success === false) {
      throw new Error(result.message || result.error || 'Email sending failed');
    }
    
    console.log('✅ Email sent successfully:', result);
    
    // Success callback
    onSuccess({
      customerEmail: emailData.customerEmail,
      customerName: emailData.customerName,
      orderNumber: emailData.orderNumber,
      environment: envInfo.environment,
      result: result
    });

    // Close modal and reset
    setIsModalOpen(false);
    setCustomMessage('');
    
    // Show success message
    alert(`Invoice email sent successfully to ${emailData.customerEmail}!`);

  } catch (err) {
    console.error('❌ Error sending email:', err);
    
    let userMessage = err.message;
    
    if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
      userMessage = `Network error (${envInfo.environment}). Please check your connection and try again.`;
    } else if (err.message.includes('404')) {
      userMessage = `Email service unavailable (${envInfo.environment}). Please try again later.`;
    } else if (err.message.includes('timeout')) {
      userMessage = 'Request timed out. Please try again.';
    } else if (err.message.includes('JSON input')) {
      userMessage = `Server response error (${envInfo.environment}). The email may have been sent. Please check and try again if needed.`;
    }
    
    setError(userMessage);
    onError({ ...err, environment: envInfo.environment, webhookUrl: finalWebhookUrl });
  } finally {
    setIsSending(false);
  }
};

const handleOpenModal = () => {
  console.log('🔍 handleOpenModal called');
  console.log('orderNumber:', orderNumber);
  console.log('customerInfo:', customerInfo);
  
  if (!orderNumber) {
    console.log('❌ No order number found');
    alert('No order number found');
    return;
  }
  
  if (!customerInfo) {
    console.log('❌ No customer information available');
    alert('No customer information available');
    return;
  }
  
  console.log('✅ Opening modal...');
  setIsModalOpen(true);
  console.log('✅ Modal state set to true');
};

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setError(null);
    setCustomMessage('');
  };

  return (
    <>
      {/* Email Button */}
<button
  onClick={(e) => {
    e.stopPropagation(); // PREVENT BUBBLING TO PARENT
    e.preventDefault();  // PREVENT DEFAULT BEHAVIOR
    console.log('Email button clicked!');
    handleOpenModal();
  }}
  disabled={disabled || !orderNumber || !customerInfo}
  className={buttonClassName}
  title={!orderNumber ? "No order number available" : 
         !customerInfo ? "No customer information available" : 
         "Send invoice via email"}
>
  {buttonText}
</button>

      {/* Email Modal */}
      {isModalOpen && createPortal(
        <div className="email-modal-overlay" onClick={(e) => e.stopPropagation()}>
                {console.log('🎭 Modal is rendering, isModalOpen:', isModalOpen)}

          <div className="email-modal">
            <div className="email-modal-header">
              <h3>{modalTitle}</h3>
              <button 
                onClick={handleCloseModal} 
                className="close-button"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="email-modal-content">
              {error ? (
                <div className="error-state">
                  <p className="error-message">❌ {error}</p>
                  <button onClick={() => setError(null)} className="retry-button">
                    Try Again
                  </button>
                </div>
              ) : (
                <div className="email-form">
                  {/* Editable Customer Info */}
                  <div className="customer-info-email">
                    <div className="info-row">
                      <label>To:</label>
                      <input
                        type="email"
                        value={emailCustomerInfo?.CustomerEmail || ''}
                        onChange={(e) => setEmailCustomerInfo(prev => ({
                          ...prev,
                          CustomerEmail: e.target.value
                        }))}
                        placeholder="customer@example.com"
                        className="customer-input"
                        required
                      />
                    </div>
                    <div className="info-row">
                      <label>Customer:</label>
                      <input
                        type="text"
                        value={emailCustomerInfo?.CustomerFullName || ''}
                        onChange={(e) => setEmailCustomerInfo(prev => ({
                          ...prev,
                          CustomerFullName: e.target.value
                        }))}
                        placeholder="Customer Name"
                        className="customer-input"
                      />
                    </div>
                    <div className="info-row">
                      <label>Order:</label>
                      <span>{orderNumber}</span>
                    </div>
                    {orderDetails?.orderType && (
                      <div className="info-row">
                        <label>Type:</label>
                        <span>{orderDetails.orderType}</span>
                      </div>
                    )}
                    
                    {/* Environment info for debugging (only show in non-production) */}
                    {!envInfo.isProduction && (
                      <div className="info-row environment-info">
                        <label>Environment:</label>
                        <span className={`env-badge env-${envInfo.environment}`}>
                          {envInfo.environment.toUpperCase()}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Custom Message */}
                  <div className="custom-message">
                    <label htmlFor="customMessage">Custom Message (Optional):</label>
                    <textarea
                      id="customMessage"
                      placeholder="Add a personal message to include with the invoice..."
                      value={customMessage}
                      onChange={(e) => setCustomMessage(e.target.value)}
                      rows="3"
                      maxLength="500"
                    />
                    <small>{customMessage.length}/500 characters</small>
                  </div>

                  {/* Action Buttons */}
                  <div className="modal-actions">
                    <button 
                      onClick={handleCloseModal}
                      className="cancel-button"
                      disabled={isSending}
                    >
                      Cancel
                    </button>
                    <button 
                      onClick={sendEmail}
                      className="send-button"
                      disabled={isSending || !emailCustomerInfo?.CustomerEmail}
                    >
                      {isSending ? (
                        <>
                          <div className="spinner small"></div>
                          Sending...
                        </>
                      ) : (
                        '📧 Send Invoice Email'
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      
      , document.body
      )}
    </>
  );
};

export default EmailInvoice;